import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { HttpClient, HttpContext } from '@angular/common/http';
import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { environment } from '../../../environments/environment';
import { HANDLE_HTTP_ERROR_LOCALLY } from '../http/http-context-tokens';
import { AuthService } from './auth/auth.service';

@Injectable({ providedIn: 'root' })
export class FaviconService {
  private readonly document = inject(DOCUMENT);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly http = inject(HttpClient);
  private readonly auth = inject(AuthService);

  private readonly saiApiUrl = environment.saiApiUrl;
  private readonly BRASAO_URL_KEY = 'des_brasao_url';
  private readonly BRASAO_ORGAO_KEY = 'des_brasao_orgao';

  aplicarBrasaoOrgao(): void {
    if (!isPlatformBrowser(this.platformId)) return;

    const idOrgao = this.auth.currentUserValue?.idOrgao;
    if (!idOrgao) return;

    const cache = this.getBrasaoCache(idOrgao);
    if (this.isValidBrasao(cache)) {
      this.updateFavicon(cache!);
      return;
    }

    // O endpoint devolve a URL em texto puro, não em JSON: sem responseType text o parse falha
    // e o Angular emite um erro com status 200. O brasão é cosmético, então o erro fica local.
    this.http
      .get(`${this.saiApiUrl}/orgao/GetBrasaoOrgao?cod_orgao_org=${idOrgao}`, {
        responseType: 'text',
        context: new HttpContext().set(HANDLE_HTTP_ERROR_LOCALLY, true),
      })
      .subscribe({
        next: (data) => {
          const brasao = this.normalize(data);
          if (!this.isValidBrasao(brasao)) return;
          this.setBrasaoCache(idOrgao, brasao!);
          this.updateFavicon(brasao!);
        },
        error: (error) => console.error('Falha ao obter o brasão do órgão', error),
      });
  }

  private getBrasaoCache(idOrgao: number): string | null {
    if (localStorage.getItem(this.BRASAO_ORGAO_KEY) !== String(idOrgao)) return null;
    return this.normalize(localStorage.getItem(this.BRASAO_URL_KEY));
  }

  private setBrasaoCache(idOrgao: number, url: string): void {
    localStorage.setItem(this.BRASAO_ORGAO_KEY, String(idOrgao));
    localStorage.setItem(this.BRASAO_URL_KEY, url);
  }

  private normalize(url: string | null): string | null {
    return url ? url.replace(/^http:\/\//i, 'https://') : url;
  }

  private isValidBrasao(url?: string | null): boolean {
    return !!url && url !== 'null' && url.trim() !== '';
  }

  private updateFavicon(iconUrl: string): void {
    let link = this.document.querySelector("link[rel*='icon']") as HTMLLinkElement | null;

    if (!link) {
      link = this.document.createElement('link');
      link.setAttribute('rel', 'icon');
      this.document.head.appendChild(link);
    }

    link.setAttribute('href', iconUrl);
  }
}
